// Admin v2 — Beállítások (Figma ADM_07, 07b, 07c). A rendszerbeállítások az app_settings/global
// dokumentumban, az API-kulcs az app_settings/api dokumentumban él (csak adminok olvashatják).
import React, { useEffect, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtInt } from '../data';
import { Button, Card, Field, Input, Notice, PageHeader, SectionTitle, Spinner, Tabs, TextArea, Toggle, useToast } from '../ui';

type Tab = 'general' | 'notifications' | 'security' | 'api';

const DEFAULTS = {
  siteName: 'Dartiqon',
  siteDescription: 'Darts közösségi és klubfoglaló platform',
  maintenanceMode: false,
  allowRegistration: true,
  requireEmailVerification: true,
  enableNotifications: true,
  emailNotifications: true,
  pushNotifications: false,
  dailyDigest: true,
  reportAlerts: true,
  twoFactorAuth: false,
  sessionTimeout: 30,
  apiRateLimit: 1000,
};
type S = typeof DEFAULTS;

const newKey = () => {
  const a = new Uint8Array(24);
  crypto.getRandomValues(a);
  return 'dq_live_' + Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
};

export default function SettingsPage() {
  const toast = useToast();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('general');
  const [saved, setSaved] = useState<S | null>(null);
  const [s, setS] = useState<S>(DEFAULTS);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'app_settings', 'global'))
      .then((d) => { const v = { ...DEFAULTS, ...(d.exists() ? d.data() : {}) } as S; setSaved(v); setS(v); })
      .catch(() => { setSaved(DEFAULTS); setS(DEFAULTS); toast('A beállítások nem tölthetők be (jogosultság).', 'red'); });
    getDoc(doc(db, 'app_settings', 'api')).then((d) => setApiKey(d.exists() ? String(d.data().key ?? '') : '')).catch(() => setApiKey(''));
  }, [toast]);

  const set = <K extends keyof S>(k: K, v: S[K]) => setS((x) => ({ ...x, [k]: v }));
  const dirty = saved && JSON.stringify(saved) !== JSON.stringify(s);

  const save = async () => {
    setBusy(true);
    try {
      await setDoc(doc(db, 'app_settings', 'global'), { ...s, updatedAt: serverTimestamp(), updatedBy: user?.uid ?? null }, { merge: true });
      setSaved(s);
      toast('Beállítások mentve.');
    } catch {
      toast('A mentés nem sikerült.', 'red');
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    const k = newKey();
    try {
      await setDoc(doc(db, 'app_settings', 'api'), { key: k, createdAt: serverTimestamp(), createdBy: user?.uid ?? null });
      setApiKey(k);
      setShowKey(true);
      toast('Új API-kulcs generálva.');
    } catch {
      toast('A kulcs nem generálható.', 'red');
    }
  };

  const Row = ({ k, title, sub }: { k: keyof S; title: string; sub: string }) => (
    <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-[#1d232a]">
      <div>
        <div className="text-[13px] font-semibold text-white">{title}</div>
        <div className="text-[11px] text-[#8a8f99] mt-0.5">{sub}</div>
      </div>
      <Toggle checked={Boolean(s[k])} onChange={(v) => set(k, v as never)} />
    </div>
  );

  const actions = (
    <>
      {tab !== 'api' && <Button kind="ghost" icon="ArrowCounterClockwise" disabled={!dirty} onClick={() => saved && setS(saved)}>Visszaállítás</Button>}
      <Button icon="Check" disabled={busy || !dirty} onClick={save}>Változtatások mentése</Button>
    </>
  );

  if (!saved) return <Spinner />;

  const masked = apiKey ? `${apiKey.slice(0, 8)}${'•'.repeat(14)}${apiKey.slice(-4)}` : '';

  return (
    <div>
      <PageHeader title="Beállítások" sub="Rendszer konfiguráció és beállítások kezelése" right={actions} />
      <Tabs<Tab> value={tab} onChange={setTab} options={[
        { value: 'general', label: 'Általános' }, { value: 'notifications', label: 'Értesítések' },
        { value: 'security', label: 'Biztonság' }, { value: 'api', label: 'API' },
      ]} />

      {tab === 'general' && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <Card className="!p-5">
              <SectionTitle>Általános beállítások</SectionTitle>
              <div className="flex flex-col gap-4">
                <Field label="Oldal neve"><Input value={s.siteName} onChange={(e) => set('siteName', e.target.value)} /></Field>
                <Field label="Oldal leírása"><TextArea rows={3} value={s.siteDescription} onChange={(e) => set('siteDescription', e.target.value)} /></Field>
              </div>
            </Card>
            <Card className="!p-5">
              <SectionTitle>Hozzáférés</SectionTitle>
              <div className="flex flex-col gap-2.5">
                <Row k="maintenanceMode" title="Karbantartási mód" sub="Oldal átmenetileg nem elérhető a felhasználók számára" />
                <Row k="allowRegistration" title="Regisztráció engedélyezése" sub="Új felhasználók regisztrálhatnak" />
                <Row k="requireEmailVerification" title="Email megerősítés kötelező" sub="Felhasználóknak meg kell erősíteniük email címüket" />
              </div>
            </Card>
          </div>
          <Card className="!p-5">
            <SectionTitle>Értesítések</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
              <Row k="enableNotifications" title="Értesítések engedélyezése" sub="Rendszer értesítések be/kikapcsolása" />
              <Row k="emailNotifications" title="Email értesítések" sub="Értesítések küldése emailben" />
              <Row k="pushNotifications" title="Push értesítések" sub="Böngésző push értesítések" />
            </div>
          </Card>
        </div>
      )}

      {tab === 'notifications' && (
        <>
          <Card className="!p-5">
            <SectionTitle>Értesítési beállítások</SectionTitle>
            <div className="flex flex-col gap-2.5">
              <Row k="enableNotifications" title="Értesítések engedélyezése" sub="Rendszer értesítések be/kikapcsolása" />
              <Row k="emailNotifications" title="Email értesítések" sub="Értesítések küldése emailben" />
              <Row k="pushNotifications" title="Push értesítések" sub="Böngésző push értesítések" />
              <Row k="dailyDigest" title="Napi összefoglaló" sub="Reggel 8:00-kor email az előző nap eseményeiről" />
              <Row k="reportAlerts" title="Jelentett tartalom riasztás" sub="Azonnali értesítés új jelentésnél" />
            </div>
          </Card>
          <div className="flex justify-end gap-3 mt-4">{actions}</div>
        </>
      )}

      {tab === 'security' && (
        <Card className="!p-5 max-w-[640px]">
          <SectionTitle>Biztonsági beállítások</SectionTitle>
          <div className="flex flex-col gap-4">
            <Row k="twoFactorAuth" title="Kétfaktoros hitelesítés" sub="Extra biztonsági réteg bejelentkezéshez" />
            <div>
              <Field label="Session timeout (perc)">
                <Input inputMode="numeric" value={String(s.sessionTimeout)} onChange={(e) => set('sessionTimeout', Number(e.target.value.replace(/[^\d]/g, '')) || 0)} />
              </Field>
              <div className="mt-2 text-[12px] text-[#8a8f99]">Automatikus kijelentkezés inaktivitás után</div>
            </div>
            <Notice>Győződj meg róla, hogy az SSL tanúsítvány érvényes és a HTTPS protokoll aktív.</Notice>
          </div>
        </Card>
      )}

      {tab === 'api' && (
        <Card className="!p-5 max-w-[720px]">
          <SectionTitle>API beállítások</SectionTitle>
          <div className="flex flex-col gap-4">
            <div>
              <Field label="API Rate Limit (kérés/óra)">
                <Input inputMode="numeric" value={fmtInt(s.apiRateLimit)} onChange={(e) => set('apiRateLimit', Number(e.target.value.replace(/[^\d]/g, '')) || 0)} />
              </Field>
              <div className="mt-2 text-[12px] text-[#8a8f99]">Maximális API kérések száma óránként felhasználónként</div>
            </div>
            <div>
              <div className="text-[13px] font-medium text-[#B8B8C3] mb-2">API kulcs</div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex-1 min-w-[220px] flex items-center gap-2.5 h-[44px] px-[15px] rounded-xl bg-[#1d232a] border border-[#31383D] text-[13px] text-white font-mono">
                  <Icon name="Keyhole" size={16} className="text-[#8a8f99]" />
                  <span className="truncate">{apiKey === null ? '…' : apiKey ? (showKey ? apiKey : masked) : 'Még nincs kulcs'}</span>
                  {apiKey && (
                    <button type="button" onClick={() => setShowKey((v) => !v)} className="ml-auto text-[#8a8f99] hover:text-white" aria-label="Kulcs mutatása">
                      <Icon name={showKey ? 'EyeSlash' : 'Eye'} size={16} />
                    </button>
                  )}
                </div>
                <Button kind="ghost" icon="Copy" disabled={!apiKey} onClick={() => { if (apiKey) { navigator.clipboard?.writeText(apiKey); toast('API-kulcs kimásolva.'); } }}>Másolás</Button>
                <Button kind="ghost" icon="ArrowsClockwise" onClick={regenerate}>{apiKey ? 'Újragenerálás' : 'Generálás'}</Button>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#00AFF2]/40 bg-[#00AFF2]/[0.06]">
              <Icon name="Info" size={18} className="text-[#00AFF2]" />
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-white">API dokumentáció</div>
                <div className="text-[12px] text-[#8a8f99]">A teljes API dokumentáció a repó DOCS mappájában érhető el.</div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
